/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-shadow */
import { Chart, LinearScale, PointElement, LineElement, LineController } from 'chart.js';
import annotationPlugin, { AnnotationOptions } from 'chartjs-plugin-annotation';
import React from 'react';
import { FormattedDate, FormattedNumber, FormattedTime } from 'react-intl';
import { EditPanelCell, EditPanelRow } from '../../elements';
import { Component } from '../../utils/Component';

Chart.register(LinearScale);
Chart.register(PointElement);
Chart.register(LineElement);
Chart.register(LineController);
Chart.register(annotationPlugin);

export function convertToMicroSeconds(thicknessInMilliMeters: number, velocityMetersPerSecond: number) {
    const distanceBetweenEchosInMilliMeters = thicknessInMilliMeters * 2;
    const distanceBetweenEchosInMeters = distanceBetweenEchosInMilliMeters / 1000;
    const timeInSeconds = 1 / (velocityMetersPerSecond / distanceBetweenEchosInMeters);
    const timeInMicroSeconds = timeInSeconds * 1000 * 1000;

    return returnPositiveNumberOrZero(timeInMicroSeconds);
}
export function returnPositiveNumberOrZero(n: number) {
    if (isNaN(n) || n === Infinity || n < 0) {
        return 0;
    }
    return n;
}
export function convertToMm(timeInMicroSeconds: number, velocityMetersPerSecond: number) {
    const timeInSeconds = timeInMicroSeconds / 1000 / 1000;
    const distanceBetweenEchosInMeters = velocityMetersPerSecond * timeInSeconds;
    const distanceBetweenEchosInMilliMeters = distanceBetweenEchosInMeters * 1000;
    const thicknessInMilliMeters = distanceBetweenEchosInMilliMeters / 2;

    return returnPositiveNumberOrZero(thicknessInMilliMeters);
}

export function convertToLength(
    timeInMicroSeconds: number,
    velocityMetersPerSecond: number | undefined,
    unitOfLength: UnitOfLength = UnitOfLength.Mm,
) {
    if (velocityMetersPerSecond === undefined) {
        return 0;
    }

    const mm = convertToMm(timeInMicroSeconds, velocityMetersPerSecond);

    if (unitOfLength === UnitOfLength.Mm) {
        return mm;
    }

    return mm / 25;
}

export enum DigitizerSamplingRate {
    F100MHz = 0,
    F50MHz = 1,
    F25MHz = 2,
}

export interface IWaveFormResult {
    measuredDateTime: string;
    data: number[];
    gate1Amp: number;
    gate1Tof: number;
    gate2Amp: number;
    gate2Tof: number;
    gate3Amp: number;
    gate3Tof: number;
    adGain: number;
    adDelay: number;
    adWidth: number;
    prMode: number;
    prPrf: number;
    prVoltage: number;
    prWidth: number;
    digitizerSamplingRate: number;
    adFilterMode: number;
    adAveraging: number;
    useGate1: boolean;
    gate1Start: number;
    gate1Width: number;
    gate1Threshold: number;
    gate1Mode: number;
    useGate2: boolean;
    gate2Start: number;
    gate2Width: number;
    gate2Threshold: number;
    gate2Mode: number;
    rectify: number;
    isConnected: boolean;
}

export enum UnitOfLength {
    Mm = 0,
    Inch = 1,
}

interface IPoint {
    length: number;
    percentage: number;
    x: number;
    y: number;
}

export interface IWaveformChartProps {
    waveFormResult: IWaveFormResult;
    velocity?: number;
    unitOfLength?: UnitOfLength;
    small?: boolean;
    onSelectPoint?: (point?: IPoint) => void;
}

interface IWaveformChartState {
    lastTooltipSelection?: IPoint;
}

export class WaveformChart extends Component<IWaveformChartProps, IWaveformChartState> {
    private canvasRef: any;
    private myChart: any;

    constructor(props: IWaveformChartProps) {
        super(props);
        this.canvasRef = React.createRef();
    }

    public override componentDidUpdate(prevProps: IWaveformChartProps) {
        if (JSON.stringify(prevProps.waveFormResult) !== JSON.stringify(this.props.waveFormResult)) {
            const formattedData = this.getData();
            this.myChart.data.datasets[0] = formattedData.datasets[0];
            this.myChart.update();
        }
    }

    public override componentDidMount() {
        const onClick = () => {
            this.props.onSelectPoint?.(this.state.lastTooltipSelection);
        };
        const t = this.props.waveFormResult;
        let maxXAxes = t.adWidth;

        const data = this.getData();

        if (this.props.velocity !== undefined) {
            const maxInMm = convertToMm(t.adWidth, this.props.velocity);
            const maxInMmRoundedToTens = Math.ceil(maxInMm / 10) * 10;
            const maxInMicroSeconds = convertToMicroSeconds(maxInMmRoundedToTens, this.props.velocity);
            maxXAxes = maxInMicroSeconds;
        }

        this.myChart = new Chart(this.canvasRef.current, {
            type: 'line',
            options: {
                events: ['click', 'mousemove'],
                onClick,
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        intersect: false,
                        displayColors: false,
                        callbacks: {
                            label: (tooltipItem) => {
                                const point = tooltipItem.raw as { x: number; y: number };
                                return this.yValueToPercentage(point.y, 2);
                            },
                            title: (tooltipItems) => {
                                const point = tooltipItems[0]!.raw as { x: number; y: number };

                                if (this.state.lastTooltipSelection?.x !== point.x) {
                                    void this.setState({
                                        lastTooltipSelection: {
                                            x: point.x,
                                            y: point.y,
                                            length: convertToLength(point.x, this.props.velocity, this.props.unitOfLength),
                                            percentage: point.y / 20,
                                        },
                                    });
                                }

                                return this.xValueToLength(point.x, 2);
                            },
                        },
                    },
                    annotation: {
                        annotations: this.getAnnotations(),
                    },
                },
                scales: {
                    x: {
                        type: 'linear',
                        ticks: {
                            callback: (valueMicroSeconds, _index, _ticks) => this.xValueToLength(valueMicroSeconds as number),
                            color: 'white',
                            stepSize: maxXAxes / 5,
                        },
                        min: 0,
                        beginAtZero: true,
                        max: maxXAxes,
                        grid: {
                            tickBorderDash: [10, 20],
                            color: 'rgba(255, 255, 255, 0.2)',
                        },
                    },
                    y: {
                        ticks: {
                            callback: (value, _index, _ticks) => this.yValueToPercentage(value as number, 0),
                            color: 'white',
                            stepSize: 500,
                        },

                        beginAtZero: true,
                        max: 2000,
                        grid: {
                            tickBorderDash: [20],
                            tickLength: 10,
                            color: 'rgba(255, 255, 255, 0.75)',
                        },
                    },
                },
            },
            data,
        });
    }

    public override render() {
        const baseClassName = 'chart-outer__container';
        const conditionalClassName = this.props.small ? 'chart-outer__container-small' : 'chart-outer__container-large';

        const finalClassName = `${baseClassName} ${conditionalClassName}`;

        return <div className={finalClassName}>{this.props.waveFormResult.data && <canvas ref={this.canvasRef} />}</div>;
    }

    private getData() {
        const t = this.props.waveFormResult;

        let itemsPerMicroSecond = 25;
        if (t.digitizerSamplingRate === DigitizerSamplingRate.F50MHz) {
            itemsPerMicroSecond = 50;
        } else if (t.digitizerSamplingRate === DigitizerSamplingRate.F100MHz) {
            itemsPerMicroSecond = 100;
        }

        const dData: { x: number; y: number }[] = [];
        for (let i = 0; i < t.data.length; i++) {
            dData.push({ y: t.data[i]!, x: i / itemsPerMicroSecond });
        }

        const data = {
            datasets: [
                {
                    label: '',
                    data: dData,
                    backgroundColor: '#000000',
                    borderColor: 'blue',
                    borderWidth: 2,
                    pointRadius: 0,
                    fill: false,
                },
            ],
        };
        return data;
    }

    private yValueToPercentage(value: number | undefined, fractionDigits: number) {
        if (value === undefined) {
            return '';
        }

        return `${(value / 20).toFixed(fractionDigits)}%`; // max is 2000, divide by 20 to get percentage
    }

    private xValueToLength(valueMicroSeconds?: number, fractionDigitsOverride?: number) {
        if (valueMicroSeconds === undefined) {
            return '';
        }

        if (this.props.velocity === undefined) {
            return `${valueMicroSeconds.toFixed(fractionDigitsOverride ?? 2)} μs`;
        }

        const valueMM = convertToMm(valueMicroSeconds, this.props.velocity);

        if (this.props.unitOfLength && this.props.unitOfLength === UnitOfLength.Inch) {
            return `${(valueMM / 25).toFixed(fractionDigitsOverride ?? 1)}"`;
        }
        return `${valueMM.toFixed(fractionDigitsOverride ?? 0)} mm`;
    }

    private getAnnotations() {
        const result: AnnotationOptions[] = [];

        if (this.props.waveFormResult.data) {
            const t = this.props.waveFormResult;

            let gate1StartXPosition = t.useGate1 ? t.gate1Start - t.adDelay : 0;

            if (gate1StartXPosition < t.adWidth) {
                let gate1EndXPosition = t.useGate1 ? t.gate1Start - t.adDelay + t.gate1Width : 0;
                const gate1Threshold = t.useGate1 ? t.gate1Threshold : 0;

                if (gate1EndXPosition >= t.adWidth) {
                    gate1EndXPosition = t.adWidth - 0.1;
                }

                if (gate1StartXPosition <= 0.1) {
                    gate1StartXPosition = 0.1;
                }

                if (gate1EndXPosition <= 0.1) {
                    gate1EndXPosition = 0.1;
                }
                result.push({
                    type: 'box',
                    drawTime: 'beforeDatasetsDraw',
                    yMin: (gate1Threshold * 2000) / 100,
                    yMax: (gate1Threshold * 2000) / 100 + 2,
                    xMin: gate1StartXPosition,
                    xMax: gate1EndXPosition,
                    backgroundColor: 'rgba(255, 0, 0, 0)',
                    borderColor: 'rgba(255, 0, 0, 0.75)',
                    borderWidth: 2,
                });

                if (t.gate1Amp > 0.01) {
                    const gate1FoundXAxis = t.gate1Tof - t.adDelay;
                    const gate1YAxis = t.gate1Amp * 20; // 100% is 2000

                    let xMin = gate1FoundXAxis - 0.2;
                    if (xMin < 0) {
                        xMin = 0.1;
                    }
                    const xMax = xMin + 0.4;

                    result.push({
                        type: 'box',
                        drawTime: 'beforeDatasetsDraw',
                        yMin: gate1YAxis,
                        yMax: gate1YAxis + 12,
                        xMin,
                        xMax,
                        backgroundColor: 'rgba(255, 0, 0, 1)',
                    });
                }
            }

            if (t.useGate2) {
                let gate2StartXPosition = t.useGate2 ? t.gate2Start - t.adDelay : 0;

                if (gate2StartXPosition < t.adWidth) {
                    let gate2EndXPosition = t.useGate2 ? t.gate2Start - t.adDelay + t.gate2Width : 0;
                    const gate2Threshold = t.useGate2 ? t.gate2Threshold : 0;

                    if (gate2StartXPosition <= 0.1) {
                        gate2StartXPosition = 0.1;
                    }

                    if (gate2EndXPosition >= t.adWidth) {
                        gate2EndXPosition = t.adWidth - 0.1;
                    }

                    if (gate2EndXPosition <= 0.1) {
                        gate2EndXPosition = 0.1;
                    }
                    result.push({
                        type: 'box',
                        drawTime: 'beforeDatasetsDraw',
                        yMin: (gate2Threshold * 2000) / 100,
                        yMax: (gate2Threshold * 2000) / 100 + 2,
                        xMin: gate2StartXPosition,
                        xMax: gate2EndXPosition,
                        backgroundColor: 'rgba(0, 255, 0, 0)',
                        borderColor: 'rgba(0, 255, 0, 0.75)',
                        borderWidth: 2,
                    });

                    if (t.gate2Amp > 0.01) {
                        const gate2FoundXAxis = t.gate2Tof - t.adDelay;
                        const gate2YAxis = t.gate2Amp * 20; // 100% is 2000

                        let xMin = gate2FoundXAxis - 0.2;
                        if (xMin <= 0) {
                            xMin = 0.1;
                        }
                        const xMax = xMin + 0.4;

                        result.push({
                            type: 'box',
                            drawTime: 'beforeDatasetsDraw',
                            yMin: gate2YAxis,
                            yMax: gate2YAxis + 12,
                            xMin,
                            xMax,
                            backgroundColor: 'rgba(0, 255, 0, 1)',
                        });
                    }
                }
            }
        }
        return result;
    }
}

export class WaveformData extends Component<IWaveformChartProps> {
    private t = this.props.waveFormResult;

    private probeMhzScale = { 0: 'None', 1: '2.5', 2: '5', 3: '10', 6: '2', 7: '4' } as any;

    public override render() {
        return (
            <>
                <EditPanelRow>
                    <EditPanelCell rowCount={2}>
                        <strong>Probe type</strong>
                    </EditPanelCell>
                    <EditPanelCell rowCount={2}>{this.t.prMode === 1 ? 'Dual' : 'Single'}</EditPanelCell>
                    <EditPanelCell rowCount={1}>&nbsp;</EditPanelCell>
                    <EditPanelCell rowCount={2}>
                        <strong>Probe Mhz</strong>
                    </EditPanelCell>
                    <EditPanelCell rowCount={4}>
                        {this.t.adFilterMode && <FormattedNumber value={parseInt(this.probeMhzScale[this.t.adFilterMode], 10)} />}
                    </EditPanelCell>
                </EditPanelRow>
                <EditPanelRow>
                    <EditPanelCell rowCount={2}>
                        <strong>Delay</strong>
                    </EditPanelCell>
                    <EditPanelCell rowCount={2}>
                        <FormattedNumber value={this.t.adDelay} /> μs
                    </EditPanelCell>
                    <EditPanelCell rowCount={1}>&nbsp;</EditPanelCell>
                    <EditPanelCell rowCount={2}>
                        <strong>Velocity</strong>
                    </EditPanelCell>
                    <EditPanelCell rowCount={4}>{this.props.velocity} m/s</EditPanelCell>
                </EditPanelRow>
                <EditPanelRow>
                    <EditPanelCell rowCount={2}>
                        <strong>Gain</strong>
                    </EditPanelCell>
                    <EditPanelCell rowCount={2}>{this.t.adGain && <FormattedNumber value={this.t.adGain} />} dB</EditPanelCell>
                    <EditPanelCell rowCount={1}>&nbsp;</EditPanelCell>
                    <EditPanelCell rowCount={2}>
                        <strong>Date</strong>
                    </EditPanelCell>
                    <EditPanelCell rowCount={4}>
                        <FormattedDate value={this.t.measuredDateTime} year="numeric" month="short" day="2-digit" />
                        &nbsp;-&nbsp;
                        <FormattedTime value={this.t.measuredDateTime} hour="numeric" minute="numeric" second="numeric" />
                    </EditPanelCell>
                </EditPanelRow>
            </>
        );
    }
}
